import React, { useState, useEffect } from 'react';
import {
  CheckCircle2, AlertCircle, AlertTriangle, XCircle,
  ChevronDown, ChevronUp, Target, TrendingUp, Shield
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import {
  ContractReadinessScore,
  ContractReadinessResult,
  ReadinessScoreComponent
} from '../../lib/contract-readiness-score';

export function ContractReadinessCard() {
  const { profile } = useAuth();
  const [result, setResult] = useState<ContractReadinessResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (profile?.id) {
      calculateReadiness();
    }
  }, [profile?.id]);

  const calculateReadiness = async () => {
    if (!profile?.id) return;

    try {
      setLoading(true);

      // Fetch all related data in parallel
      const [
        documentsRes,
        pastPerformanceRes,
        personnelRes,
        complianceRes,
        pricingRes
      ] = await Promise.all([
        supabase
          .from('set_aside_documents')
          .select('set_aside_type, document_url, expiration_date')
          .eq('profile_id', profile.id),
        supabase
          .from('past_performance')
          .select('id, agency, sector_type, contract_value, cpars_overall_rating, contract_number')
          .eq('profile_id', profile.id),
        supabase
          .from('key_personnel')
          .select('security_clearance, certifications')
          .eq('profile_id', profile.id),
        supabase
          .from('compliance_documents')
          .select('document_type, expiration_date, status')
          .eq('profile_id', profile.id),
        supabase
          .from('enhanced_pricing_data')
          .select('id, naics_codes')
          .eq('profile_id', profile.id)
      ]);

      const readinessResult = ContractReadinessScore.calculate(
        profile,
        documentsRes.data || [],
        pastPerformanceRes.data || [],
        personnelRes.data || [],
        complianceRes.data || [],
        pricingRes.data || []
      );

      setResult(readinessResult);
    } catch (error) {
      console.error('Error calculating readiness:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: ReadinessScoreComponent['status']) => {
    switch (status) {
      case 'complete':
        return <CheckCircle2 className="h-5 w-5 text-success" />;
      case 'partial':
        return <AlertCircle className="h-5 w-5 text-fit-moderate" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-fit-moderate" />;
      case 'missing':
        return <XCircle className="h-5 w-5 text-urgent" />;
    }
  };

  const getReadinessColor = (level: ContractReadinessResult['readinessLevel']) => {
    switch (level) {
      case 'Excellent':
        return 'text-success bg-success-bg border-success-border';
      case 'Good':
        return 'text-bw-primary bg-bw-primary-tint border-bw-primary/20';
      case 'Fair':
        return 'text-fit-moderate bg-fit-moderate-bg border-fit-moderate-border';
      case 'Poor':
        return 'text-fit-moderate bg-orange-50 border-orange-200';
      case 'Not Ready':
        return 'text-urgent bg-urgent-bg border-urgent-border';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'bg-success-bg0';
    if (score >= 60) return 'bg-bw-primary-tint0';
    if (score >= 40) return 'bg-fit-moderate-bg0';
    if (score >= 20) return 'bg-orange-500';
    return 'bg-urgent-bg0';
  };

  if (loading) {
    return (
      <div className="bg-bw-surface rounded-lg shadow p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-20 bg-gray-200 rounded mb-4"></div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  if (!result) return null;

  return (
    <div className="bg-bw-surface rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-bw-ink flex items-center">
            <Target className="h-5 w-5 mr-2 text-bw-primary" />
            Federal Contract Readiness
          </h3>
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getReadinessColor(result.readinessLevel)}`}>
            {result.readinessLevel}
          </span>
        </div>

        {/* Score Display */}
        <div className="flex items-center space-x-4">
          <div className="relative w-24 h-24">
            <svg className="w-24 h-24 transform -rotate-90">
              <circle
                cx="48"
                cy="48"
                r="40"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-gray-200"
              />
              <circle
                cx="48"
                cy="48"
                r="40"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={251.2}
                strokeDashoffset={251.2 - (251.2 * result.totalScore) / 100}
                className={result.totalScore >= 60 ? 'text-success' : result.totalScore >= 40 ? 'text-fit-moderate' : 'text-urgent'}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-bw-ink">{result.totalScore}</span>
            </div>
          </div>

          <div className="flex-1">
            <p className="text-sm text-bw-muted mb-2">
              {result.isContractReady
                ? 'Your profile is ready for federal contract opportunities.'
                : 'Complete the items below to improve your contract readiness.'}
            </p>
            {result.criticalMissing.length > 0 && (
              <div className="flex items-center text-urgent text-sm">
                <AlertTriangle className="h-4 w-4 mr-1" />
                {result.criticalMissing.length} critical item(s) missing
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Component Breakdown */}
      <div className="p-6">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between text-sm font-medium text-bw-ink mb-4"
        >
          <span>Score Breakdown</span>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {expanded && (
          <div className="space-y-4">
            {result.components.map((component, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    {getStatusIcon(component.status)}
                    <span className="ml-2 font-medium text-bw-ink">{component.name}</span>
                    <span className="ml-2 text-xs text-bw-muted">({component.weight}%)</span>
                  </div>
                  <span className="font-semibold text-bw-ink">{component.score}%</span>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getScoreColor(component.score)}`}
                    style={{ width: `${component.score}%` }}
                  />
                </div>

                <p className="text-xs text-bw-muted mb-2">{component.details}</p>

                {component.actionItems.length > 0 && component.status !== 'complete' && (
                  <div className="mt-2 space-y-1">
                    {component.actionItems.slice(0, 2).map((item, i) => (
                      <div key={i} className="flex items-start text-xs text-bw-muted">
                        <TrendingUp className="h-3 w-3 mr-1 mt-0.5 text-bw-primary flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!expanded && result.recommendations.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-bw-ink mb-2">Top Recommendations:</p>
            {result.recommendations.slice(0, 3).map((rec, index) => (
              <div key={index} className="flex items-start text-sm text-bw-muted">
                <Shield className="h-4 w-4 mr-2 mt-0.5 text-bw-primary flex-shrink-0" />
                <span>{rec}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-bw-surface-subtle border-t">
        <p className="text-xs text-bw-muted">
          This score measures your readiness for federal contracts, not just profile completeness.
          Focus on the critical items first to unlock more opportunities.
        </p>
      </div>
    </div>
  );
}
